import {
  Button,
  Input,
  Modal,
  Radio,
  Select,
  Skeleton,
  Space,
  Typography,
} from "antd";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { HelpLine } from "~/components/help/helpLine.jsx";
import { config } from "~/config.js";

const { Option } = Select;
const { Search } = Input;
const { Text } = Typography;

const COUNTRIES = [
  { code: "NL", name: "Nederland" },
  { code: "GB", name: "Engeland" },
  { code: "BE", name: "Belgie" },
  { code: "DK", name: "Denemarken" },
  { code: "DE", name: "Duitsland" },
  { code: "FR", name: "Frankrijk" },

  // $t('Nederland')
  // $t('Engeland')
  // $t('Belgie')
  // $t('Denemarken')
  // $t('Duitsland')
  // $t('Frankrijk')
];

export function AddAirport({ onSelectAirport }) {
  const { club } = useSelector((state) => state.persist);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [airports, setAirports] = useState([]);
  const [filteredAirports, setFilteredAirports] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Sort countries with club country on top
  const sortedCountries = useMemo(() => {
    const clubCountry = club?.country;
    if (!clubCountry) return COUNTRIES;

    const clubCountryItem = COUNTRIES.find((c) => c.code === clubCountry);
    const otherCountries = COUNTRIES.filter((c) => c.code !== clubCountry);

    return clubCountryItem ? [clubCountryItem, ...otherCountries] : COUNTRIES;
  }, [club?.country]);

  // Load airports when country is selected
  useEffect(() => {
    const loadAirports = async () => {
      if (selectedCountry) {
        setLoading(true);
        setSelectedAirport(null);
        const data = await api.get(
          null,
          `${config.base_backend_url}/all/airports/?country=${selectedCountry}`,
        );
        const airportData = data || [];
        // sort airportData by name
        airportData.sort((a, b) => a.name.localeCompare(b.name));
        setAirports(airportData);
        setFilteredAirports(airportData);
        setLoading(false);
      } else {
        setAirports([]);
        setFilteredAirports([]);
      }
    };

    loadAirports();
  }, [selectedCountry]);

  // Filter airports based on search text
  useEffect(() => {
    if (searchText) {
      const filtered = airports.filter(
        (airport) =>
          airport.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          airport.description?.toLowerCase().includes(searchText.toLowerCase()),
      );
      setFilteredAirports(filtered);
    } else {
      setFilteredAirports(airports);
    }
  }, [searchText, airports]);

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setSelectedAirport(null);
    setSearchText("");
  };

  const handleAirportSelect = (e) => {
    const airportCode = e.target.value;
    const airport = airports.find((ap) => ap.name === airportCode);
    setSelectedAirport(airport);
  };

  const handleSelectAirport = () => {
    if (selectedAirport && onSelectAirport) {
      onSelectAirport(selectedAirport.name);
      setModalOpen(false);
      // Reset state
      setSelectedCountry(null);
      setSelectedAirport(null);
      setSearchText("");
    }
  };

  const handleCancel = () => {
    setModalOpen(false);
    // Reset state
    setSelectedCountry(null);
    setSelectedAirport(null);
    setSearchText("");
  };

  return (
    <>
      <Button type="dashed" onClick={() => setModalOpen(true)}>
        {$t("Voeg OGN vliegveld toe")}
      </Button>

      <Modal
        title={$t("Voeg OGN vliegveld toe")}
        open={modalOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {$t("Annuleren")}
          </Button>,

          <Button
            key="select"
            type="primary"
            onClick={handleSelectAirport}
            disabled={!selectedAirport}
          >
            {$t("Selecteer")}
          </Button>,
        ]}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <HelpLine>
            {$t(
              "Je kan hier een vliegveld toevoegen. Indien je veld niet in de lijst staat, dan kan je er alsnog op vliegen alleen niet met automatische tijdschrijven. Neem contact met ons op als je een vliegveld wilt toevoegen die niet in de lijst staat.",
            )}
          </HelpLine>

          <div>
            <Text strong>{$t("Selecteer land:")}</Text>
            <Select
              style={{ width: "100%", marginTop: 8 }}
              placeholder={$t("Kies een land")}
              value={selectedCountry}
              onChange={handleCountryChange}
            >
              {sortedCountries.map((country) => (
                <Option key={country.code} value={country.code}>
                  {$t(country.name)}
                </Option>
              ))}
            </Select>
          </div>

          {selectedCountry && (
            <div>
              <Text strong>{$t("Zoek vliegveld:")}</Text>
              <Search
                style={{ marginTop: 8 }}
                placeholder={$t("Zoek op naam of code")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                loading={loading}
              />
            </div>
          )}

          {loading && selectedCountry && (
            <div>
              <Text strong>{$t("Selecteer vliegveld:")}</Text>
              <div style={{ marginTop: 8 }}>
                <Skeleton active paragraph={{ rows: 3 }} />
              </div>
            </div>
          )}

          {!loading && filteredAirports.length > 0 && (
            <div>
              <Text strong>{$t("Selecteer vliegveld:")}</Text>
              <Radio.Group
                style={{ width: "100%", marginTop: 8 }}
                value={selectedAirport?.name}
                onChange={handleAirportSelect}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {filteredAirports.map((airport) => (
                    <Radio key={airport.name} value={airport.name}>
                      <div>
                        <Text strong>{airport.name}</Text>
                        <br />
                        <Text type="secondary">{airport.description}</Text>
                      </div>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </div>
          )}

          {selectedCountry && !loading && filteredAirports.length === 0 && (
            <Text type="secondary">
              {searchText
                ? $t("Geen vliegvelden gevonden voor deze zoekopdracht")
                : $t("Geen vliegvelden gevonden voor dit land")}
            </Text>
          )}
        </Space>
      </Modal>
    </>
  );
}
